import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, CalendarCheck } from "lucide-react";
import { BUSINESS, scrollToId } from "../config/business";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#0b0c0e]"
    >
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <img
          src="/images/hero.jpg"
          alt="Mechanic servicing a vehicle at Auto Trust Centre in Mississauga"
          className="h-[116%] w-full object-cover"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0b0c0e]/95 via-[#0b0c0e]/70 to-[#0b0c0e]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c0e] via-transparent to-[#0b0c0e]/50" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-40 lg:px-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="font-display mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#00AEEF] sm:text-sm"
        >
          <span className="inline-block h-[3px] w-10 bg-[#00AEEF]" />
          Independent Auto Repair · Mississauga, ON
        </motion.p>

        <h1 className="font-display text-balance text-5xl font-extrabold uppercase leading-[0.98] tracking-tight text-[#f4f4f2] sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.25}>Auto Repair</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-[#00AEEF]">You Can Trust.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-[#c9ccd2] sm:text-lg"
        >
          Professional automotive repair, maintenance and diagnostics in
          Mississauga. Straightforward advice, quality workmanship and service
          focused on keeping your vehicle safe and reliable.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-9 flex flex-col gap-4 sm:flex-row"
        >
          <button
            data-testid="hero-book-button"
            onClick={() => scrollToId("appointment")}
            className="btn-primary"
          >
            <CalendarCheck className="h-5 w-5" />
            Book an Appointment
          </button>
          <a data-testid="hero-call-button" href={BUSINESS.phoneHref} className="btn-ghost">
            <Phone className="h-5 w-5 text-[#00AEEF]" />
            Call {BUSINESS.phone}
          </a>
        </motion.div>
      </div>
    </section>
  );
};
